// Smart Sash Admin Panel - Session Card Component

import { Button } from '@/components/common';
import { ActiveSessionInfo, SessionAccessInfo } from '@/models/teaching.models';
import React, { useMemo, useState } from 'react';

interface SessionCardProps {
  session: ActiveSessionInfo;
  accessInfo?: SessionAccessInfo;
  onJoin: (session: ActiveSessionInfo) => void;
}

function formatElapsedTime(startedAt: string): string {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - start);

  const hours = Math.floor(diff / 3_600_000);
  const minutes = Math.floor((diff % 3_600_000) / 60_000);
  const seconds = Math.floor((diff % 60_000) / 1_000);

  return [hours, minutes, seconds].map((n) => String(n).padStart(2, '0')).join(':');
}

function getSessionGradient(seed: string): React.CSSProperties {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue1 = Math.abs(hash % 360);
  const hue2 = (hue1 + 50) % 360;
  return {
    background: `linear-gradient(135deg, hsl(${hue1}, 55%, 20%) 0%, hsl(${hue2}, 65%, 12%) 100%)`,
  };
}

export const SessionCard: React.FC<SessionCardProps> = ({ session, accessInfo, onJoin }) => {
  const [showDetails, setShowDetails] = useState(false);
  const elapsed = useMemo(() => formatElapsedTime(session.startedAt), [session.startedAt]);
  const canJoin = !accessInfo || accessInfo.canJoin;
  const tooltip = accessInfo && !accessInfo.canJoin ? accessInfo.reason : undefined;
  const gradientStyle = useMemo(
    () => getSessionGradient(session.labName || session.streamSessionId),
    [session.labName, session.streamSessionId]
  );
  const initials = (session.labName || 'Lab').slice(0, 2).toUpperCase();

  return (
    <div className="card h-100 shadow-sm hover-shadow overflow-hidden">
      {/* Thumbnail preview */}
      <div
        className="position-relative d-flex align-items-center justify-content-center"
        style={{ height: '150px', ...gradientStyle }}
      >
        <div className="text-center" style={{ pointerEvents: 'none' }}>
          <div
            className="text-white fw-bold"
            style={{ fontSize: '2.8rem', opacity: 0.15, letterSpacing: '4px' }}
          >
            {initials}
          </div>
          <i className="fas fa-video text-white" style={{ fontSize: '1.8rem', opacity: 0.3 }} />
        </div>

        {/* Live badge */}
        <span
          className="position-absolute top-0 end-0 m-2 badge bg-danger d-flex align-items-center gap-1"
          style={{ fontSize: '0.68rem' }}
        >
          <span className="ss-live-dot" />
          Live
        </span>

        {/* Elapsed time */}
        <span
          className="position-absolute bottom-0 end-0 m-2 badge bg-black bg-opacity-50 text-white"
          style={{ fontSize: '0.68rem', fontVariantNumeric: 'tabular-nums' }}
        >
          <i className="fas fa-clock me-1" style={{ fontSize: '0.55rem' }} />
          {elapsed}
        </span>

        {/* Observer count */}
        <span
          className="position-absolute bottom-0 start-0 m-2 badge bg-black bg-opacity-50 text-white"
          style={{ fontSize: '0.68rem' }}
        >
          <i className="fas fa-eye me-1" style={{ fontSize: '0.55rem' }} />
          {session.observersCount} {session.observersCount === 1 ? 'observer' : 'observers'}
        </span>
      </div>

      <div className="card-body d-flex flex-column p-3">
        {/* Title */}
        <h6 className="card-title mb-1 fw-semibold text-truncate" title={session.streamTitle}>
          {session.streamTitle || 'Untitled Stream'}
        </h6>

        {/* Lab & Presenter */}
        <div className="text-muted small mb-3">
          <div className="d-flex align-items-center gap-1 mb-1">
            <i className="fas fa-flask" style={{ width: '14px', flexShrink: 0 }} />
            <span className="text-truncate">{session.labName}</span>
          </div>
          <div className="d-flex align-items-center gap-1">
            <i className="fas fa-user" style={{ width: '14px', flexShrink: 0 }} />
            <span className="text-truncate">{session.presenterName}</span>
          </div>
        </div>

        <div className="mt-auto">
          {/* Technical details expander */}
          <button
            type="button"
            className="btn btn-link btn-sm p-0 text-muted text-decoration-none d-flex align-items-center gap-1 mb-2"
            onClick={() => setShowDetails((v) => !v)}
          >
            <i
              className={`fas fa-chevron-${showDetails ? 'up' : 'down'}`}
              style={{ fontSize: '0.6rem' }}
            />
            <span style={{ fontSize: '0.72rem' }}>Technical details</span>
          </button>

          {showDetails && (
            <div
              className="rounded p-2 mb-2"
              style={{ background: 'var(--ss-bg-tertiary)', fontSize: '0.7rem' }}
            >
              <div className="mb-1">
                <span className="text-muted">Session </span>
                <code className="user-select-all" style={{ wordBreak: 'break-all' }}>
                  {session.streamSessionId}
                </code>
              </div>
              <div className="mb-1">
                <span className="text-muted">Device </span>
                <code className="user-select-all" style={{ wordBreak: 'break-all' }}>
                  {session.deviceId}
                </code>
              </div>
              <div className="mb-1">
                <span className="text-muted">Lab </span>
                <code className="user-select-all" style={{ wordBreak: 'break-all' }}>
                  {session.labId}
                </code>
              </div>
              <div className="mb-1">
                <span className="text-muted">Presenter ID </span>
                <code className="user-select-all" style={{ wordBreak: 'break-all' }}>
                  {session.presenterUserId}
                </code>
              </div>
              <div>
                <span className="text-muted">Started </span>
                {new Date(session.startedAt).toLocaleString()}
              </div>
            </div>
          )}

          <div title={tooltip}>
            <Button
              variant="primary"
              className="w-100"
              onClick={() => onJoin(session)}
              disabled={!canJoin}
              icon="fas fa-play"
            >
              Join as Observer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
